'use client';

import { useState, useMemo, useEffect } from 'react';
import styles from './styles/modals.module.css';

// ---------- Utility Helpers ----------
const pad = (n) => String(n).padStart(2, '0');

/**
 * Parse singleClassDuration string to get minutes
 * Input could be: "60 minutes", "1 hours", "90 minutes", "60", etc.
 * @param {string|number} durationInput - Duration string or number
 * @returns {number} Duration in minutes
 */
const parseClassDuration = (durationInput) => {
  if (typeof durationInput === 'number') return durationInput;
  if (!durationInput) return 60; // default

  const durationStr = String(durationInput);
  const match = durationStr.match(/(\d+)\s*(hour|minute|min|hr)?/i);
  if (!match) return parseInt(durationStr) || 60;

  const value = parseInt(match[1]);
  const unit = match[2]?.toLowerCase() || '';

  if (unit.startsWith('hour') || unit.startsWith('hr')) {
    return value * 60; // Convert hours to minutes
  }
  return value; // Already minutes or no unit specified
};

/**
 * Add minutes to a HH:MM time string
 * @param {string} hhmm - Time in HH:MM format (e.g., "17:00")
 * @param {number|string} mins - Minutes to add
 * @returns {string} New time in HH:MM format
 */
const addMinutesToHHMM = (hhmm, mins) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const minutesToAdd = Number(mins) || 0;
  const totalMinutes = h * 60 + m + minutesToAdd;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  return `${pad(newHours)}:${pad(newMinutes)}`;
};

/**
 * Format HH:MM time to 12-hour AM/PM format
 * @param {string} hhmm - Time in HH:MM format (e.g., "17:00")
 * @returns {string} Time in 12-hour format (e.g., "5:00 PM")
 */
const formatTimeAMPM = (hhmm) => {
  if (!hhmm || hhmm === '--:--') return '--:--';
  const [hours, minutes] = hhmm.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '--:--';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${pad(minutes)} ${ampm}`;
};
const toDate = (s) => (s ? new Date(s + 'T00:00:00') : null);
const bumpDate = (date, days) => {
  const d = toDate(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};
const minutesDiff = (a, b) => {
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  return (bh * 60 + bm) - (ah * 60 + am);
};

// Day name to day number mapping (0=Sunday, 1=Monday, etc.)
const dayNameToNum = {
  'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
  'Thursday': 4, 'Friday': 5, 'Saturday': 6
};

// Days of week for selection UI
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Get the date of a specific day of the week starting from a given date
 * @param {Date} startDate - The section start date
 * @param {number} targetDayNum - Target day number (0=Sunday, 6=Saturday)
 * @param {number} weeksOffset - Number of weeks to add
 * @returns {Date} The date of the target day
 */
const getNextDayOfWeek = (startDate, targetDayNum, weeksOffset) => {
  const result = new Date(startDate);
  const currentDayNum = result.getDay();

  // Calculate days until next occurrence of target day
  let daysUntilTarget = targetDayNum - currentDayNum;
  if (daysUntilTarget < 0) {
    daysUntilTarget += 7; // Next week
  }

  result.setDate(result.getDate() + daysUntilTarget + (weeksOffset * 7));
  return result;
};

// ---------- Component ----------
export default function AddSectionModal({
  isOpen,
  onClose,
  onSave,
  sidebarHints = {
    weeklyFrequency: 1,
    courseDurationWeeks: 10,
    singleClassDurationMinutes: 60,
    teachingDays: [] // Array of day names, e.g., ['Monday', 'Wednesday']
  },
  editMode = false,
  initialData = null,
  sectionId = null,
  userRole = null,
}) {
  // Expected total from Sidebar (freq × weeks)
  const totalMeetingsExpected = sidebarHints.weeklyFrequency * sidebarHints.courseDurationWeeks;

  // Parse the singleClassDurationMinutes properly - it may be a string like "60 minutes" or "1 hours"
  const parsedDuration = parseClassDuration(sidebarHints.singleClassDurationMinutes);

  const [classDurationMinutes, setClassDurationMinutes] = useState(
    editMode
      ? initialData?.classDurationMinutes || parsedDuration
      : parsedDuration
  );

  const [meetings, setMeetings] = useState(() => {
    if (editMode && initialData?.meetings?.length > 0) {
      // Map meetings to local format
      const mappedMeetings = initialData.meetings.map((m) => {
        const start = new Date(m.startTime);
        const end = new Date(m.endTime);
        return {
          id: 0, // Will be reassigned after sorting
          day: '', // Day name not tracked in edit mode
          date: start.toISOString().split('T')[0],
          startTime: start.toTimeString().slice(0, 5),
          endTime: end.toTimeString().slice(0, 5),
          _originalStart: start, // Temp field for sorting
        };
      });
      // Sort chronologically by startTime
      mappedMeetings.sort((a, b) => a._originalStart - b._originalStart);
      // Reassign IDs and remove temp field
      return mappedMeetings.map((m, i) => {
        const { _originalStart, ...rest } = m;
        return { ...rest, id: i + 1 };
      });
    }
    // Start with empty meetings - will be populated when user selects days
    return [];
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sectionStartDate, setSectionStartDate] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [uniformStartTime, setUniformStartTime] = useState('');

  // Day selection toggle function
  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day]
    );
  };

  // Validation for day count
  const requiredDays = sidebarHints?.weeklyFrequency || 1;
  const isValidDayCount = selectedDays.length === requiredDays;

  // Day name to number mapping for validation
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Validate that start date falls on a selected teaching day
  useEffect(() => {
    if (!sectionStartDate || !selectedDays?.length) {
      setStartDateError('');
      return;
    }

    const date = new Date(sectionStartDate + 'T00:00:00');
    const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.
    const selectedDayName = dayNames[dayOfWeek];

    if (!selectedDays.includes(selectedDayName)) {
      setStartDateError(`Start date must be a ${selectedDays.join(' or ')}`);
    } else {
      setStartDateError('');
    }
  }, [sectionStartDate, selectedDays]);

  // Clear meetings when selectedDays changes (will be regenerated with Generate button)
  useEffect(() => {
    if (!editMode && selectedDays.length > 0) {
      // Reset meetings - they will be generated when user clicks Generate
      setMeetings([]);
    }
  }, [selectedDays, editMode]);

  const handleMeetingChange = (id, field, value) => {
    setMeetings((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        const updated = { ...m, [field]: value };

        // Auto-derive end time when start changes
        if (field === 'startTime') {
          updated.endTime = addMinutesToHHMM(value, classDurationMinutes);
        }

        return updated;
      })
    );
  };

  const addMeeting = () =>
    setMeetings((prev) => [
      ...prev,
      { id: prev.length + 1, date: '', startTime: '', endTime: '' },
    ]);

  const removeMeeting = (id) =>
    setMeetings((prev) =>
      prev.length > 1 ? prev.filter((m) => m.id !== id) : prev
    );

  // Derived stats
  const stats = useMemo(() => {
    const valid = meetings.filter((m) => m.date && m.startTime && m.endTime);
    const duration = valid.length
      ? Math.round(
        valid.reduce((a, b) => a + minutesDiff(b.startTime, b.endTime), 0) /
        valid.length
      )
      : classDurationMinutes;
    return { total: valid.length, duration };
  }, [meetings, classDurationMinutes]);

  const countMatches = meetings.length === totalMeetingsExpected;

  // ---------- Comprehensive Validation Errors ----------
  const validationErrors = useMemo(() => {
    const errors = [];

    // Check meeting count matches expected
    if (meetings.length !== totalMeetingsExpected) {
      errors.push(`Meeting count (${meetings.length}) must equal ${totalMeetingsExpected} (${sidebarHints.weeklyFrequency}x/week x ${sidebarHints.courseDurationWeeks} weeks)`);
    }

    // In non-edit mode: check selectedDays count matches requiredDays
    if (!editMode && selectedDays.length !== requiredDays) {
      errors.push(`Select exactly ${requiredDays} teaching day${requiredDays !== 1 ? 's' : ''} (currently ${selectedDays.length} selected)`);
    }

    // In non-edit mode: check uniform start time is set when days are selected but meetings not generated
    if (!editMode && selectedDays.length === requiredDays && meetings.length === 0 && !uniformStartTime) {
      errors.push('Enter a meeting start time');
    }

    // Check each meeting for completeness and valid times
    meetings.forEach((m, index) => {
      const meetingLabel = `Meeting ${index + 1}`;

      // Check for missing fields
      if (!m.date) {
        errors.push(`${meetingLabel}: Missing date`);
      }
      if (!m.startTime) {
        errors.push(`${meetingLabel}: Missing start time`);
      }
      if (!m.endTime) {
        errors.push(`${meetingLabel}: Missing end time`);
      }

      // Check endTime is after startTime (only if both exist)
      if (m.startTime && m.endTime) {
        const startMinutes = parseInt(m.startTime.split(':')[0]) * 60 + parseInt(m.startTime.split(':')[1]);
        const endMinutes = parseInt(m.endTime.split(':')[0]) * 60 + parseInt(m.endTime.split(':')[1]);
        if (endMinutes <= startMinutes) {
          errors.push(`${meetingLabel}: End time must be after start time`);
        }
      }
    });

    return errors;
  }, [meetings, totalMeetingsExpected, editMode, selectedDays, requiredDays, uniformStartTime, sidebarHints.weeklyFrequency, sidebarHints.courseDurationWeeks]);

  const isFormValid = validationErrors.length === 0;

  // ---------- Safety Validation ----------
  const validateMeetings = () => {
    if (totalMeetingsExpected <= 0) {
      alert('This course has 0 expected meetings based on the sidebar. Please update the course info first.');
      return false;
    }

    // Count must match expected
    if (meetings.length !== totalMeetingsExpected) {
      alert(
        `The total number of meetings must equal ${totalMeetingsExpected} (based on ${sidebarHints.weeklyFrequency}× per week × ${sidebarHints.courseDurationWeeks} weeks).`
      );
      return false;
    }

    for (const m of meetings) {
      if (!m.date || !m.startTime || !m.endTime) {
        alert('Please fill in all fields (date, start time, and end time).');
        return false;
      }

      const startDateTime = new Date(`${m.date}T${m.startTime}`);
      const endDateTime = new Date(`${m.date}T${m.endTime}`);
      const now = new Date();

      if (startDateTime < now && !editMode && userRole !== 'SUPER_ADMIN') {
        alert('Meeting times cannot be in the past.');
        return false;
      }

      if (endDateTime <= startDateTime) {
        alert('End time must be after start time.');
        return false;
      }
    }
    return true;
  };

  // Bulk-generate all meetings based on selectedDays and section start date
  const handleAutoGenerate = () => {
    const { courseDurationWeeks } = sidebarHints;

    // Validation: check section start date
    if (!sectionStartDate) {
      alert('Please select a section start date first.');
      return;
    }

    // Helper: format date as YYYY-MM-DD in local time (avoids timezone issues with toISOString)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // If selectedDays configured (new flow), use them
    if (isValidDayCount) {
      // Validate uniform start time is entered
      if (!uniformStartTime) {
        alert('Please enter a meeting start time.');
        return;
      }

      const startTime = uniformStartTime;
      const endTime = addMinutesToHHMM(startTime, classDurationMinutes);

      // Generate all meetings, ensuring they're on or after the start date
      const result = [];
      const startDate = new Date(sectionStartDate + 'T00:00:00');
      startDate.setHours(0, 0, 0, 0); // Normalize to midnight local time
      const totalMeetingsNeeded = courseDurationWeeks * selectedDays.length;
      let weekOffset = 0;

      // Generate meetings week by week until we have enough
      while (result.length < totalMeetingsNeeded && weekOffset < 100) { // Safety limit
        for (const dayName of selectedDays) {
          if (result.length >= totalMeetingsNeeded) break;

          const dayNum = dayNameToNum[dayName];
          const meetingDate = getNextDayOfWeek(startDate, dayNum, weekOffset);

          // Normalize meetingDate to midnight for comparison
          meetingDate.setHours(0, 0, 0, 0);

          // Only include meetings on or after the section start date
          if (meetingDate >= startDate) {
            result.push({
              id: result.length + 1,
              day: dayName,
              date: formatLocalDate(meetingDate),
              startTime,
              endTime,
            });
          }
        }
        weekOffset++;
      }

      // Sort meetings by date chronologically
      result.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Re-assign IDs after sorting
      result.forEach((meeting, index) => {
        meeting.id = index + 1;
      });

      setMeetings(result);
    } else {
      // Legacy flow: use first week's pattern (for courses without teachingDays)
      if (totalMeetingsExpected <= 0) {
        alert('This course has 0 expected meetings based on the sidebar. Please update the course info first.');
        return;
      }

      if (!meetings[0].date) {
        alert('Please enter at least one meeting date to use as the first week pattern.');
        return;
      }

      const pattern = meetings.filter((m) => m.date);
      const result = [];
      let count = 0;
      let weekOffset = 0;

      while (count < totalMeetingsExpected) {
        for (const p of pattern) {
          if (count >= totalMeetingsExpected) break;
          const newDate = bumpDate(p.date, weekOffset * 7);
          result.push({
            id: count + 1,
            day: '',
            date: newDate,
            startTime: p.startTime,
            endTime: addMinutesToHHMM(p.startTime, classDurationMinutes),
          });
          count++;
        }
        weekOffset++;
      }
      setMeetings(result);
    }
  };

  // Optimistic Save with Validation + Auto-refresh on success
  const handleSubmit = async () => {
    if (!validateMeetings()) return;

    setIsSubmitting(true);

    const formattedMeetings = meetings.map((m) => ({
      startTime: new Date(`${m.date}T${m.startTime}`).toISOString(),
      endTime: new Date(`${m.date}T${m.endTime}`).toISOString(),
    }));

    const sectionData = {
      classDurationMinutes,
      weeklyFrequency: sidebarHints.weeklyFrequency,
      courseDurationWeeks: sidebarHints.courseDurationWeeks,
      meetings: formattedMeetings,
      ...(editMode && sectionId ? { sectionId } : {}),
      ...(selectedDays.length > 0 ? { teachingDays: selectedDays.join(',') } : {}),
    };

    // Instant close for fast UX
    onClose();

    try {
      await onSave(sectionData);

      // ✅ Auto-refresh the page so the new/updated section appears
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.reload();
        }, 300); // small delay so modal can close smoothly
      }

      // If you use Next.js App Router, you could alternatively do:
      // const router = useRouter();  // import from 'next/navigation'
      // router.refresh();

    } catch (err) {
      console.error('❌ Section save failed:', err);
      alert(err?.message || 'Something went wrong while saving the section.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal} onClick={onClose}>
      <div
        className={styles.doubleColumnModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.close} onClick={onClose}>
          &times;
        </span>
        <h2>{editMode ? 'Edit Class Section' : 'Create Class Section'}</h2>

        {/* Two-column layout for non-edit mode */}
        {!editMode ? (
          <div className={styles.entriesContainer}>
            {/* Left Column: Meeting Schedule */}
            <div className={styles.formColumn}>
              <h3>Meetings Schedule</h3>

              {/* Show time inputs for each selected day */}
              {isValidDayCount ? (
                <>
                  {/* Section Start Date */}
                  <div className={styles.inputGroup}>
                    <label style={{ fontWeight: 'bold' }}>Section Start Date:</label>
                    <input
                      type="date"
                      value={sectionStartDate}
                      onChange={(e) => setSectionStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      style={{ marginLeft: '8px' }}
                    />
                    {startDateError && (
                      <span style={{ color: '#dc3545', fontSize: '14px', marginLeft: '10px' }}>
                        {startDateError}
                      </span>
                    )}
                  </div>

                  <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                    Meeting Time (applies to all days):
                  </h4>
                  <div className={styles.inputGroup}>
                    <label style={{ fontWeight: 'bold' }}>Start Time:</label>
                    <input
                      type="time"
                      value={uniformStartTime}
                      onChange={(e) => setUniformStartTime(e.target.value)}
                      style={{ marginLeft: '8px' }}
                    />
                    <span style={{ marginLeft: '10px', color: '#666' }}>
                      ends at {uniformStartTime ? formatTimeAMPM(addMinutesToHHMM(uniformStartTime, classDurationMinutes)) : '--:--'}
                    </span>
                  </div>

                  {/* Generate All Meetings button */}
                  <div className={styles.section} style={{ marginTop: '1rem' }}>
                    <button
                      onClick={handleAutoGenerate}
                      className={styles.btn}
                      style={{ backgroundColor: '#28a745', color: 'white' }}
                      disabled={!sectionStartDate || !!startDateError || !uniformStartTime}
                    >
                      Generate All {totalMeetingsExpected} Meetings
                    </button>
                    {!sectionStartDate && (
                      <span style={{ marginLeft: '10px', color: '#dc3545', fontSize: '14px' }}>
                        (Select start date first)
                      </span>
                    )}
                    {sectionStartDate && !uniformStartTime && (
                      <span style={{ marginLeft: '10px', color: '#dc3545', fontSize: '14px' }}>
                        (Enter start time)
                      </span>
                    )}
                    {sectionStartDate && uniformStartTime && startDateError && (
                      <span style={{ marginLeft: '10px', color: '#dc3545', fontSize: '14px' }}>
                        (Fix date error first)
                      </span>
                    )}
                  </div>

                  {/* Show generated meetings preview */}
                  {meetings.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <h4>Generated Meetings Preview:</h4>
                      <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', padding: '8px', borderRadius: '4px' }}>
                        {meetings.map((m) => (
                          <div key={m.id} style={{ fontSize: '13px', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                            <strong>{m.day || 'Meeting'}</strong> - {m.date} at {formatTimeAMPM(m.startTime)} - {formatTimeAMPM(m.endTime)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', color: '#6c757d' }}>
                  <p style={{ margin: 0 }}>
                    Please select {requiredDays} day{requiredDays !== 1 ? 's' : ''} from the right panel first.
                  </p>
                </div>
              )}

              {/* Live counter + guidance */}
              <div style={{ marginTop: '0.5rem', fontSize: 14 }}>
                <strong>Meetings added:</strong> {meetings.length} / {totalMeetingsExpected}
                {meetings.length !== totalMeetingsExpected && (
                  <span style={{ color: '#dc3545', marginLeft: 8 }}>
                    (Add or remove to match the expected total)
                  </span>
                )}
              </div>
            </div>

            {/* Right Column: Day Selection */}
            <div className={styles.rightColumn}>
              <h3>Section Schedule</h3>
              <label className={styles.formLabel}>
                Which days will this section meet?
              </label>
              <p className={styles.helperText}>
                Select exactly {requiredDays} day{requiredDays !== 1 ? 's' : ''}
              </p>
              <div className={styles.daysGrid}>
                {DAYS_OF_WEEK.map((day) => (
                  <label key={day} className={styles.dayCheckbox}>
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day)}
                      onChange={() => toggleDay(day)}
                    />
                    <span>{day}</span>
                  </label>
                ))}
              </div>
              {selectedDays.length > 0 && (
                <p className={styles.selectedCount}>
                  Selected: {selectedDays.length} / {requiredDays}
                  {isValidDayCount ? ' ✓' : ''}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Edit mode: Single column layout */
          <div className={styles.section}>
            <h3>Meetings Schedule</h3>
            {meetings.map((m) => (
              <div key={m.id} className={styles.inputGroup}>
                <label>Date:</label>
                <input
                  type="date"
                  value={m.date}
                  onChange={(e) => handleMeetingChange(m.id, 'date', e.target.value)}
                />

                <label>Start:</label>
                <input
                  type="time"
                  value={m.startTime}
                  onChange={(e) => handleMeetingChange(m.id, 'startTime', e.target.value)}
                />

                <label>End:</label>
                <input
                  type="time"
                  value={m.endTime}
                  onChange={(e) => handleMeetingChange(m.id, 'endTime', e.target.value)}
                />

                {meetings.length > 1 && (
                  <button
                    onClick={() => removeMeeting(m.id)}
                    className={styles.btn}
                    style={{ backgroundColor: '#dc3545', color: '#fff' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={addMeeting}
              className={styles.btn}
              style={{ backgroundColor: '#6c757d', color: 'white', marginTop: '0.5rem' }}
            >
              Add A Meeting (Manually)
            </button>

            {/* Live counter + guidance */}
            <div style={{ marginTop: '0.5rem', fontSize: 14 }}>
              <strong>Meetings added:</strong> {meetings.length} / {totalMeetingsExpected}
              {meetings.length !== totalMeetingsExpected && (
                <span style={{ color: '#dc3545', marginLeft: 8 }}>
                  (Add or remove to match the expected total)
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error Feedback Section */}
        {validationErrors.length > 0 && (
          <div className={styles.errorFeedback}>
            <strong>Please fix the following issues:</strong>
            <ul>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormValid}
            style={{
              opacity: (isSubmitting || !isFormValid) ? 0.5 : 1,
              cursor: (isSubmitting || !isFormValid) ? 'not-allowed' : 'pointer'
            }}
          >
            {editMode ? 'Update Section' : 'Save Section'}
          </button>
          <button
            onClick={onClose}
            className={styles.btn}
            style={{ backgroundColor: '#6c757d', color: '#fff' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
